import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { netSuiteInvoicingService, NetSuiteOrder, GahhImportItem } from '../../api/services/netSuiteInvoicingService';

const NetSuiteInvoicing: React.FC = () => {
  const [orders, setOrders] = useState<NetSuiteOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [importing, setImporting] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadOrders();

    // Auto-refresh every 5 minutes (300000ms)
    const refreshInterval = setInterval(() => {
      loadOrders();
    }, 300000);

    return () => clearInterval(refreshInterval);
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await netSuiteInvoicingService.getOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a valid CSV file.');
      return;
    }

    try {
      setImporting(true);
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      // Skip header row
      const dataLines = lines.slice(1);

      const items: GahhImportItem[] = [];
      for (const line of dataLines) {
        const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
        if (parts.length >= 3) {
          const orderId = parseInt(parts[0]);
          if (!isNaN(orderId)) {
            items.push({
              OrderId: orderId,
              GahhOrderNumber: parts[1],
              GahhInvoiceNumber: parts[2]
            });
          }
        }
      }

      if (items.length === 0) {
        alert('No valid data found in the CSV file.');
        return;
      }

      const result = await netSuiteInvoicingService.importGahhInfo(items);
      alert(`Import complete!\nSuccessful: ${result.success}\nFailed: ${result.failed}`);

      // Reload orders
      await loadOrders();
    } catch (error) {
      console.error('Error importing file:', error);
      alert('An error occurred while importing the file.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getStatusBadgeClass = (actionName: string, gahhOrderNumber: string | null, gahhInvoiceNumber: string | null) => {
    // If has invoice number, it's completed
    if (gahhInvoiceNumber) {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    // If has order number but no invoice, awaiting invoice
    if (gahhOrderNumber && !gahhInvoiceNumber) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
    // Ready - misc/drop-ship only order
    if (actionName === 'Ready') {
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200';
    }
    // Label Printed - ready for receiving
    if (actionName === 'Label Printed') {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    }
    // Received - ready to ship
    if (actionName === 'Received') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
    // Item shipped but no GAHH info yet
    if (actionName === 'Item SHIPPED') {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const getStatusLabel = (actionName: string, gahhOrderNumber: string | null, gahhInvoiceNumber: string | null) => {
    if (gahhInvoiceNumber) {
      return 'Completed';
    }
    if (gahhOrderNumber && !gahhInvoiceNumber) {
      return 'Awaiting Invoice';
    }
    if (actionName === 'Ready') {
      return 'Ready';
    }
    if (actionName === 'Label Printed') {
      return 'Label Printed';
    }
    if (actionName === 'Received') {
      return 'Received - Ready';
    }
    if (actionName === 'Item SHIPPED') {
      return 'Shipped - Pending';
    }
    return actionName;
  };

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto" style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                NetSuite Order Invoicing
              </h2>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-lg text-gray-900 dark:text-white">{orders.length}</span> orders
                </div>
                <button
                  onClick={loadOrders}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Refreshing...
                    </>
                  ) : (
                    'Refresh'
                  )}
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Import Section */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Import GAHH Info
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Upload a CSV file with the following columns: Order ID, GAHH Order #, GAHH Invoice #
                </p>
                <details className="mb-3">
                  <summary className="cursor-pointer text-sm text-blue-600 dark:text-blue-400">
                    View Sample Format
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded text-sm overflow-x-auto">
{`OrderId,GAHH SO #,GAHH Invoice #
1047591,SO351196,INV669530
1047593,SO351192,INV669532
1047627,SO351238,INV670718`}
                  </pre>
                </details>
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    disabled={importing}
                    className="block text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-200"
                  />
                  {importing && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Importing...
                    </span>
                  )}
                </div>
              </div>

              {/* Orders Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">Order ID</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">Bill To</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">Customer Order No.</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">Status</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">Ship Date</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">Payment Terms</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">GAHH Order #</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-white">GAHH Invoice #</th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-900 dark:text-white">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-400">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                          Loading...
                        </td>
                      </tr>
                    ) : orders.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-400">
                          No data found.
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr key={order.OrderId} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">{order.OrderId}</td>
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">{order.BillToName}</td>
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">{order.CustomerOrderNo}</td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(order.ActionName, order.GahhOrderNumber, order.GahhInvoiceNumber)}`}>
                              {getStatusLabel(order.ActionName, order.GahhOrderNumber, order.GahhInvoiceNumber)}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">
                            {order.CreateDate ? format(new Date(order.CreateDate), 'M/d/yyyy h:mm a') : '-'}
                          </td>
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">{order.Terms || '-'}</td>
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">{order.GahhOrderNumber || '-'}</td>
                          <td className="py-3 px-3 text-gray-900 dark:text-gray-100">{order.GahhInvoiceNumber || '-'}</td>
                          <td className="py-3 px-3 text-right text-gray-900 dark:text-gray-100">
                            ${order.Total.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default NetSuiteInvoicing;
